import { Component, Inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {FraudActionTaken, FraudState, FraudType, StateChangeReasonKey} from "@app2/clients/types/alert-typedefs";
import * as _ from "underscore";

interface DialogData {
    currentState: string;
    currentTriggerName: string;
}

type FraudItem = {
    id: string;
    value: string;
    localizeKey: string;
};

type FraudActionItem = {
    id: string;
    value: string;
    localizeKey: string;
    checked: boolean;
};

@Component({
    selector: "flag-fraud-form-modal",
    template: `
        <span mat-dialog-title>{{ "FLAG_FRAUD_MODAL_TITLE" | localize }}</span>
        <mat-dialog-content>
            <article>
                <p id="fraud-header-header" [innerHTML]="'FRAUD_MODAL_HEADER' | localize:this.currentTriggerName"></p>
                <form #f="ngForm" class="nice-form" test-class="flag-fraud-form">
                    <h1 class="sub-header">{{ "THIS_ALERT_IS" | localize }}</h1>
                    <section>
                        <div *ngFor="let fraudState of fraudStates">
                            <input type="radio" id="{{ fraudState.id }}" name="fraud-type-state"
                                   value="{{ fraudState.value }}" [(ngModel)]="fraudReasonKey" required>
                            <label for="{{ fraudState.id }}">{{ fraudState.localizeKey | localize }}</label>
                        </div>
                    </section>
                    <h1 class="sub-header">{{ "TYPE_OF_FRAUD" | localize }}</h1>
                    <section>
                        <div *ngFor="let fraudCategory of fraudTypes">
                            <input type="radio" id="{{ fraudCategory.id }}" name="fraud-flag-type"
                                   value="{{ fraudCategory.value }}"
                                   [attr.disabled]="((fraudCategory.id === 'other' && this.fraudReasonKey === 'FRAUD_SUSPECTED') || (fraudCategory.id === 'unknown' && this.fraudReasonKey === 'FRAUD_CONFIRMED')) || null"
                                   [(ngModel)]="fraudType" required>
                            <label for="{{ fraudCategory.id }}">{{ fraudCategory.localizeKey | localize }}</label>
                        </div>
                    </section>
                    <h1 class="sub-header">{{ "ACTION_TAKEN" | localize }}</h1>
                    <section>
                        <div *ngFor="let fraudAction of fraudActions">
                            <label>
                                <input type="checkbox" id="{{ fraudAction.id }}" name="fraud-flag-action" value="{{fraudAction.value}}"
                                       style="vertical-align:middle;"
                                       [(ngModel)]="fraudAction.checked">
                                {{ fraudAction.localizeKey | localize }}
                            </label>
                        </div>
                    </section>
                    <h1 class="sub-header">{{ "ADDITIONAL_DETAILS" | localize }}</h1>
                    <section>
                    <textarea id="additional-detail" name="additional-detail" [(ngModel)]="detail"
                              rows="5"></textarea>
                    </section>
                </form>
            </article>
        </mat-dialog-content>
        <mat-dialog-actions>
            <modal-buttons [canSave]="f.valid"
                           (onCancel)="onCancel()"
                           (onSave)="onSave()">
            </modal-buttons>
        </mat-dialog-actions>
    `,
})

export class FlagFraudFormModalComponent {
    fraudReasonKey: string;
    fraudType: string;
    detail: string;
    currentState: string;
    currentTriggerName: string;

    fraudStates: FraudItem[] = [
        { id: "suspected-fraud", value: FraudState.fraudSuspected, localizeKey: "FRAUD_SUSPECTED" },
        { id: "confirmed-fraud", value: FraudState.fraudConfirmed, localizeKey: "FRAUD_CONFIRMED" },
    ];

    fraudTypes: FraudItem[] = [
        { id: "new-account", value: FraudType.newAccount, localizeKey: "NEW_ACCOUNT" },
        { id: "account-takeover", value: FraudType.accountTakeover, localizeKey: "ACCOUNT_TAKEOVER" },
        { id: "scam", value: FraudType.scam, localizeKey: "SCAM" },
        { id: "bill-pay", value: FraudType.billPay, localizeKey: "BILL_PAY" },
        { id: "debit-card", value: FraudType.debitCard, localizeKey: "DEBIT_CARD" },
        { id: "credit-card", value: FraudType.creditCard, localizeKey: "CREDIT_CARD" },
        { id: "check-fraud", value: FraudType.checkFraud, localizeKey: "CHECK_FRAUD" },
        { id: "other", value: FraudType.other, localizeKey: "OTHER" },
        { id: "unknown", value: FraudType.unknown, localizeKey: "UNKNOWN" },
    ];

    fraudActions: FraudActionItem[] = [
        { id: "account-restricted", value: FraudActionTaken.accountRestricted, localizeKey: "ACCOUNT_RESTRICTED",
            checked: false},
        { id: "card-reissued", value: FraudActionTaken.cardReissued, localizeKey: "CARD_REISSUED", checked: false},
        { id: "customer-refund", value: FraudActionTaken.customerRefund, localizeKey: "CUSTOMER_REFUND",
            checked: false},
        { id: "customer-not-refunded", value: FraudActionTaken.customerNotRefunded,
            localizeKey: "CUSTOMER_NOT_REFUNDED", checked: false},
        { id: "institution-contacted", value: FraudActionTaken.institutionContacted,
            localizeKey: "INSTITUTION_CONTACTED", checked: false},
        { id: "suspect-details-captured", value: FraudActionTaken.suspectDetailsCaptured,
            localizeKey: "SUSPECT_DETAILS_CAPTURED", checked: false},
        { id: "restitution-account-opened", value: FraudActionTaken.restitutionAccountOpened,
            localizeKey: "RESTITUTION_ACCOUNT_OPENED", checked: false},
    ];


    constructor(private dialogRef: MatDialogRef<FlagFraudFormModalComponent>,
                @Inject(MAT_DIALOG_DATA) public data: DialogData) {
        this.currentState = data.currentState;
        this.currentTriggerName = data.currentTriggerName;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onSave(): void {
        this.dialogRef.close({
            oldState: this.currentState,
            newState: StateChangeReasonKey.flagFraud,
            reasonKey: this.fraudReasonKey,
            fraudType: this.fraudType,
            fraudActionTaken: _.filter(this.fraudActions, (fraudActionTaken) => fraudActionTaken.checked)
                .map((checkedAction) => checkedAction.value),
            detail: this.detail || undefined,
        });
    }
}
